import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { saveExtractedData } from '../api/dataInputApi';
import { useOcrInputStore } from '../store/ocrInputStore';
import { createSavePayload } from '../utils/createSavePayload';
import { useCallback } from 'react';
import { trackEvent } from '../../../modules/analytics/trackingMiddleware';
import { OCR_DATA_SUBMITTED } from '../../../modules/analytics/events';

export const useSaveData = () => {
  const navigate = useNavigate();
  const { extractedData, dataInputId, selectedDateTime, resetOcrData, selectedTemplate, selectedAsset } = useOcrInputStore();

  const mutationFn = useCallback(async () => {
    if (!dataInputId || !extractedData || !selectedDateTime) {
      throw new Error("Cannot save: Missing required data, or date & time is not set.");
    }

    if (selectedDateTime > new Date().getTime()) {
      throw new Error("Cannot save: Date & time is in the future.");
    }

    const configurations = createSavePayload(extractedData);

    if (configurations.length === 0) {
      toast.warn('There is no data linked to a sensor. Nothing to save.');
      throw new Error('NO_DATA_TO_SAVE');
    }

    return saveExtractedData({
      dataInputId,
      configurations,
      logbookTimestamp: selectedDateTime!
    });
  }, [dataInputId, extractedData, selectedDateTime]);

  const onSuccess = useCallback(
    (data: any) => {
      trackEvent(OCR_DATA_SUBMITTED, { dataInputId: dataInputId || '', templateId: selectedTemplate?.logbookTemplateId || '', assetId: selectedAsset?.assetId || '', entryCount: extractedData?.length || 0 });
      toast.success(data.message || 'Data saved successfully!');
      navigate(-1);
    },
    [navigate, resetOcrData, dataInputId]
  );

  const onError = useCallback((error: Error) => {
    if (error.message === 'NO_DATA_TO_SAVE') {
      return;
    }
    toast.error(error.message || 'Failed to save the data. Please try again.');
  }, []);

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess,
    onError,
  });

  return {
    saveData: mutate,
    isSaving: isPending,
  };
};
